import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { HiArrowLeft } from "react-icons/hi2";
import { HiOutlineSearch, HiOutlineExternalLink } from "react-icons/hi";
import { LuImageDown } from "react-icons/lu";
import ExtractionHistoryDropdown from "./ExtractionHistoryDropdown";
import * as MetadataView from "../features/metadataView";

// Unified Dynamic Island style info pill with extraction progress
const TableInfoPill = ({
  favicon,
  title,
  url,
  rowCount,
  searchTerm,
  isExtracting,
  extractionStatus,
  elapsedTime,
  truncateUrl,
}) => {
  const isStarting = extractionStatus === 'pending';

  return (
    <div className="relative flex-1 min-w-0">
      {/* Animated glow effect - only when extracting */}
      {isExtracting && (
        <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 via-emerald-400/10 to-emerald-500/20 rounded-full blur-md opacity-75 animate-pulse-glow" />
      )}

      {/* Main pill container */}
      <div className={`relative flex items-center gap-3 px-4 py-2 rounded-full transition-all duration-500 ${
        isExtracting
          ? 'bg-gradient-to-r from-white/[0.04] via-emerald-950/50 to-emerald-950/60 border border-emerald-500/30 shadow-lg shadow-emerald-500/5'
          : 'bg-white/[0.04] border border-white/[0.06]'
      }`}>
        {/* Left section: extraction indicator + favicon + title + url */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Extraction indicator - before favicon when extracting */}
          {isExtracting && (
            <>
              <div className="flex items-center gap-2 shrink-0">
                {/* Animated dots */}
                <div className="flex items-center gap-0.5">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-emerald-400"
                      style={{
                        animation: 'extraction-dot 1.4s ease-in-out infinite',
                        animationDelay: `${i * 0.15}s`,
                      }}
                    />
                  ))}
                </div>
                <span className="text-xs font-medium text-emerald-300 whitespace-nowrap">
                  {isStarting ? 'Starting...' : 'Extracting'}
                </span>
                {elapsedTime && (
                  <span className="text-xs text-emerald-400/70 tabular-nums font-medium">
                    {elapsedTime}
                  </span>
                )}
              </div>
              <div className="w-px h-4 bg-emerald-500/30 shrink-0" />
            </>
          )}

          {favicon && (
            <img
              src={favicon}
              alt=""
              className="w-4 h-4 rounded shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}

          {title && (
            <span className="text-sm font-medium truncate min-w-0 text-gray-200">
              {title}
            </span>
          )}

          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:flex items-center gap-1 text-xs transition-colors min-w-0 text-gray-500 hover:text-gray-300"
              title={url}
            >
              <span className="truncate">{truncateUrl(url, 40)}</span>
              <HiOutlineExternalLink className="w-3 h-3 flex-shrink-0" />
            </a>
          )}
        </div>

        {/* Divider */}
        <div className={`w-px h-4 transition-colors duration-300 ${isExtracting ? 'bg-emerald-500/20' : 'bg-white/10'}`} />

        {/* Right section: row count + extraction status */}
        <div className="flex items-center gap-3 shrink-0">
          {/* Row count */}
          <div className="flex items-center gap-1.5">
            <span className={`text-sm font-semibold tabular-nums transition-colors duration-300 ${
              isExtracting ? 'text-emerald-100' : 'text-white'
            }`}>
              {rowCount.toLocaleString()}
            </span>
            <span className="text-sm text-gray-500">
              {searchTerm ? 'matching' : 'rows'}
            </span>
          </div>

        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes extraction-dot {
          0%, 100% {
            opacity: 0.4;
            transform: scale(0.75);
          }
          50% {
            opacity: 1;
            transform: scale(1.1);
          }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// Compact extraction pill for mobile
const MobileExtractionPill = ({ status, elapsedTime }) => {
  const isStarting = status === 'pending';

  return (
    <div className="relative shrink-0">
      <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/30 via-emerald-400/20 to-emerald-500/30 rounded-full blur-md opacity-75 animate-pulse-glow" />
      <div className="relative flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-b from-emerald-950/90 to-emerald-950/80 border border-emerald-500/30 shadow-lg shadow-emerald-500/10">
        <div className="flex items-center gap-0.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1 h-1 rounded-full bg-emerald-400"
              style={{
                animation: 'extraction-dot 1.4s ease-in-out infinite',
                animationDelay: `${i * 0.15}s`,
              }}
            />
          ))}
        </div>
        <span className="text-[10px] font-medium text-emerald-300 whitespace-nowrap">
          {isStarting ? 'Starting' : 'Live'}
        </span>
        {elapsedTime && (
          <>
            <div className="w-px h-2.5 bg-emerald-500/30" />
            <span className="text-[10px] text-emerald-400/80 tabular-nums font-medium">
              {elapsedTime}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

// Reusable icon button with tooltip
const IconButton = ({
  icon,
  tooltip,
  onClick,
  disabled = false,
  active = false,
  variant = "default" // "default" | "primary" | "success"
}) => {
  const variants = {
    default: active
      ? "bg-white/10 text-white"
      : "text-gray-400 hover:text-white hover:bg-white/[0.06]",
    primary: active
      ? "bg-blue-500/20 text-blue-400"
      : "text-blue-400 hover:text-blue-300 hover:bg-blue-500/10",
    success: active
      ? "bg-emerald-500/20 text-emerald-400"
      : "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10",
  };

  return (
    <div className="relative group/btn">
      <button
        onClick={onClick}
        disabled={disabled}
        aria-label={tooltip || undefined}
        className={`w-8 h-8 flex items-center justify-center rounded-md transition-all duration-150 ${
          disabled ? "text-gray-600 cursor-not-allowed" : variants[variant]
        }`}
      >
        {icon}
      </button>
      {tooltip && (
        // Hidden where hovering isn't possible: on a phone the hover state
        // sticks to the last tapped button, which would park the tooltip over
        // the content.
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 invisible group-hover/btn:opacity-100 group-hover/btn:visible transition-all duration-200 pointer-events-none z-50 bg-[#1a1a1c] text-gray-200 border border-white/[0.08] shadow-xl [@media(hover:none)]:hidden">
          {tooltip}
        </div>
      )}
    </div>
  );
};

// Button group container
const ButtonGroup = ({ children, className = "" }) => (
  <div className={`flex items-center gap-0.5 p-1 rounded-lg bg-white/[0.03] ${className}`}>
    {children}
  </div>
);

const TableHeader = ({
  selectedTableDataId,
  loading,
  toggleTheme,
  isDarkMode,
  exportMenu,
  automationRunning,
  autoRefreshEnabled,
  tableData,
  data,
  filteredData,
  totalRows,
  searchTerm,
  setSearchTerm,
  isSearching = false,
  onRunAutomation,
  onTrackImageDownload,
  taskId,
  onTableSelect,
  onOpenImageDownload,
  canDownloadImages,
  filters,
  toggleFilter,
  taskRunStatus,
  taskRunStartedAt,
  isAdmin,
  // Product mode (mobile-only grid view). The toggle only renders on phones
  // for tables that have image columns.
  showProductModeToggle = false,
  isProductMode = false,
  onToggleProductMode,
}) => {
  const navigate = useNavigate();

  // Elapsed time tracking
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Update elapsed time every second based on task run start time
  useEffect(() => {
    if (!taskRunStartedAt || !automationRunning || !autoRefreshEnabled) {
      setElapsedSeconds(0);
      return;
    }

    // Calculate initial elapsed time
    const updateElapsed = () => {
      const elapsed = Math.floor((Date.now() - taskRunStartedAt.getTime()) / 1000);
      setElapsedSeconds(Math.max(0, elapsed));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [taskRunStartedAt, automationRunning, autoRefreshEnabled]);

  // Format elapsed time as "Xm Ys" or "Xs"
  const formatElapsedTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const displayRowCount = totalRows !== undefined && totalRows !== null
    ? totalRows
    : (filteredData ? filteredData.length : data?.length || 0);

  const sourceTask = tableData?.metadata?.sourceTask;
  const sourceUrl = sourceTask?.url || tableData?.metadata?.url;

  const truncateUrl = (url, maxLength = 30) => {
    if (!url) return "";
    try {
      const urlObj = new URL(url);
      const displayUrl = urlObj.hostname + urlObj.pathname;
      if (displayUrl.length <= maxLength) return displayUrl;
      return displayUrl.substring(0, maxLength - 3) + "...";
    } catch {
      if (url.length <= maxLength) return url;
      return url.substring(0, maxLength - 3) + "...";
    }
  };

  const formatRelativeTime = useMemo(() => {
    const createdAt = tableData?.createdAt;
    if (!createdAt) return null;

    const date = new Date(createdAt);
    if (isNaN(date.getTime())) return null;

    const now = new Date();
    const diffMs = now - date;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "1 day ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffWeeks === 1) return "1 week ago";
    if (diffWeeks < 4) return `${diffWeeks} weeks ago`;
    if (diffMonths === 1) return "1 month ago";
    return `${diffMonths} months ago`;
  }, [tableData?.createdAt]);

  const handleDownloadImages = () => {
    if (onOpenImageDownload) {
      onOpenImageDownload();
    } else if (onRunAutomation) {
      onRunAutomation("downloadImages");
      if (onTrackImageDownload) {
        onTrackImageDownload(tableData, data);
      }
    }
  };

  // Icons
  const ImageIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );

  const SunIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );

  const GridIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
    </svg>
  );

  const MoonIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  );


  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3 sticky top-0 z-[100] bg-[#0a0a0b] border-b border-white/[0.06]">
      {/* Left: Logo + Table info */}
      <div className="flex items-center gap-4 flex-1 min-w-0">
        {/* Logo */}
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2.5 group relative"
        >
          <div className="w-9 h-9 rounded-xl p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-md shadow-cyan-500/15 group-hover:shadow-cyan-500/30 group-hover:scale-105 transition-all duration-200">
            <div className="w-full h-full bg-white rounded-[10px] p-0.5 flex items-center justify-center overflow-hidden relative">
              <img
                src="/logo-128.png"
                alt="Logo"
                className="w-full h-full absolute transition-all duration-300 opacity-100 group-hover:opacity-0 group-hover:scale-75"
              />
              <HiArrowLeft className="w-5 h-5 text-blue-500 absolute transition-all duration-300 opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100" />
            </div>
          </div>
          <div className="hidden sm:flex flex-col gap-0 items-start">
            <p className="text-[8px] uppercase tracking-[0.2em] font-semibold text-gray-500 leading-tight transition-opacity duration-300 group-hover:opacity-0">
              Ultimate Web Scraper
            </p>
            <h1 className="text-sm font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent tracking-tight leading-none transition-opacity duration-300 group-hover:opacity-0">
              Cloud Data
            </h1>
            <span className="absolute left-12 text-sm font-semibold text-blue-500 dark:text-blue-400 opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap">
              Go to Dashboard
            </span>
          </div>
        </button>

        {/* Unified Dynamic Island info pill - desktop */}
        {tableData && (
          <div className="hidden md:flex flex-1 min-w-0">
            <TableInfoPill
              favicon={sourceTask?.favicon}
              title={sourceTask?.name}
              url={sourceUrl}
              rowCount={displayRowCount}
              searchTerm={searchTerm}
              isExtracting={automationRunning && autoRefreshEnabled}
              extractionStatus={taskRunStatus}
              elapsedTime={elapsedSeconds > 0 ? formatElapsedTime(elapsedSeconds) : null}
              truncateUrl={truncateUrl}
            />
          </div>
        )}

        {/* Mobile extraction pill - only shows when extracting */}
        {automationRunning && autoRefreshEnabled && (
          <div className="md:hidden">
            <MobileExtractionPill
              status={taskRunStatus}
              elapsedTime={elapsedSeconds > 0 ? formatElapsedTime(elapsedSeconds) : null}
            />
          </div>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
        {/* Search - collapsible on mobile */}
        <div className="relative group hidden sm:block">
          <HiOutlineSearch className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${
            isSearching ? 'text-blue-400' : 'text-gray-500 group-focus-within:text-gray-400'
          }`} />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-28 lg:w-36 pl-9 pr-8 py-2 text-sm rounded-lg transition-all duration-200 focus:w-48 focus:outline-none focus:ring-2 bg-white/[0.04] border text-white placeholder-gray-500 focus:bg-white/[0.06] focus:border-white/[0.1] focus:ring-white/[0.08] ${
              searchTerm && !isSearching ? 'border-blue-500/30' : 'border-white/[0.06]'
            }`}
          />
          {isSearching && (
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
              <div className="w-3.5 h-3.5 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
            </div>
          )}
          {searchTerm && !isSearching && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full text-xs text-gray-500 hover:text-gray-300 hover:bg-white/10 transition-colors"
            >
              ×
            </button>
          )}
        </div>

        {/* Mobile search icon button */}
        <div className="sm:hidden">
          <ButtonGroup>
            <IconButton
              icon={<HiOutlineSearch className="w-4 h-4" />}
              tooltip="Search"
              onClick={() => {
                const result = window.prompt('Search rows:', searchTerm);
                if (result !== null) setSearchTerm(result);
              }}
              active={!!searchTerm}
            />
          </ButtonGroup>
        </div>

        {/* View Settings Group */}
        <ButtonGroup>
          {showProductModeToggle && (
            <IconButton
              icon={<GridIcon />}
              tooltip={isProductMode ? "Table view" : "Product mode"}
              onClick={onToggleProductMode}
              disabled={!onToggleProductMode}
              active={isProductMode}
              variant={isProductMode ? "primary" : "default"}
            />
          )}
          <IconButton
            icon={<ImageIcon />}
            tooltip={filters?.includeImages ? "Hide images" : "Show images"}
            onClick={() => toggleFilter?.("includeImages")}
            disabled={!toggleFilter}
            active={filters?.includeImages}
          />
          {MetadataView.ENABLED && isAdmin && (
            <IconButton
              icon={<MetadataView.MetadataIcon />}
              tooltip={filters?.showMetadataView ? "Show data" : "Show debug info"}
              onClick={() => toggleFilter?.("showMetadataView")}
              disabled={!toggleFilter}
              active={filters?.showMetadataView}
            />
          )}
          <IconButton
            icon={isDarkMode ? <SunIcon /> : <MoonIcon />}
            tooltip={isDarkMode ? "Light mode" : "Dark mode"}
            onClick={toggleTheme}
          />
        </ButtonGroup>

        {/* Data Export Group - hidden on very small screens */}
        <ButtonGroup className="hidden xs:flex">
          <IconButton
            icon={<LuImageDown className="w-4 h-4" />}
            tooltip="Download images"
            onClick={handleDownloadImages}
            disabled={loading || !selectedTableDataId || !canDownloadImages}
            variant="primary"
          />
        </ButtonGroup>

        {/* History & Export */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {taskId && (
            <ExtractionHistoryDropdown
              taskId={taskId}
              currentTableId={selectedTableDataId}
              onTableSelect={onTableSelect}
              isDarkMode={true}
              relativeTime={formatRelativeTime}
            />
          )}
          {exportMenu}
        </div>
      </div>
    </div>
  );
};

export default TableHeader;
